import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EmailIcon from '@mui/icons-material/MailOutlineRounded';
import { Box, Collapse, Divider, IconButton, Stack, Typography } from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import moment from "moment";
import React, { Fragment, memo, useMemo, useState } from "react";
import { ReactComponent as Email } from "../../../assets/icons/email.svg";
import useRoute from '../../../hooks/useRoute';
import { scrollbarStyle } from "../../../shared/dashboard";
import { Dashboard, EmailBody } from "../../../types/files";
import ArrayUtils from "../../../utils/ArrayUtils";
import EmailContent from "../dashboard-query-answer/EmailContent";

const useStyles = makeStyles((theme) => ({
    card: {
        position: 'relative',
        width: 500,
        maxWidth: 500,
        height: '100%',
        maxHeight: 500,
        padding: 16,
        paddingRight: 24,
        overflow: 'hidden',
        borderRadius: 16,
        backgroundColor: '#fff',
        border: `1px solid ${theme.colors.neutral['100']}`,
        boxShadow: '0px 3px 5px -1px rgba(16, 24, 40,0.2), 0px 5px 8px 0px rgba(16, 24, 40,0.14), 0px 1px 14px 0px rgba(16, 24, 40,0.12)',
        transition: '0.05s ease-in-out',
    },
    body: {
        width: '100%',
        height: '100%',
        overflowX: 'hidden',
        overflowY: 'auto',
        ...scrollbarStyle,
    },
    title: {
        display: '-webkit-box',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: 2,
        wordBreak: 'break-word',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        fontFamily: 'Poppins',
        fontSize: '1.2rem',
        fontWeight: '600',
        color: theme.colors.neutral['500'],
    },
    subtitle: {
        fontFamily: 'Inter',
        fontSize: '0.95rem',
        fontWeight: 'bold',
        color: theme.colors.neutral['600'],
    },
    subject: {
        display: '-webkit-box',
        width: 'fit-content',
        fontFamily: 'Poppins',
        fontSize: '1rem',
        fontWeight: 'bold',
        color: '#666666',
        overflow: 'hidden',
        WebkitLineClamp: 1,
        WebkitBoxOrient: 'vertical',
        textOverflow: 'ellipsis',
        textTransform: 'capitalize',
        maxWidth: '40ch',
        wordBreak: 'break-all',
    },
    ownership: {
        fontFamily: 'Inter',
        fontSize: '1rem',
        fontWeight: '500',
        color: theme.colors.neutral['500'],
    },
    owner: {
        fontFamily: 'Inter',
        fontSize: '1rem',
        fontWeight: 'bold',
        color: theme.palette.primary.main,
    },
    timestamp: {
        fontFamily: 'Inter',
        fontSize: '0.75rem',
        fontWeight: 500,
        color: theme.colors.neutral['600'],
        textAlign: 'right',
    },
    icon: {
        width: 28,
        height: 28,
    },
    label: {
        fontFamily: 'Poppins',
        fontSize: '1.2rem',
        fontWeight: 'bold',
        color: theme.palette.primary.main,
        "&:hover": {
            textDecoration: 'underline',
        }
    },
}));

const EmailsContentCard: React.FC<{ dashboard: Dashboard }> = ({ dashboard }) => {
    const classes = useStyles();
    const { redirectToDashboard } = useRoute();
    const [isExpanded, setExpanded] = useState<boolean[]>([true]);

    const emailActivities = useMemo(() => {
        const activityItems: EmailBody[] = dashboard?.emailBodies
            ?.filter(emailBody => ['EMAIL', 'FULL_EMAIL'].includes(emailBody?.type || '')) as EmailBody[];

        return ArrayUtils.sortByDescending(activityItems ?? [], 'createdAt');
    }, [dashboard]);

    if (!emailActivities.length)
        return (<></>);

    return (<>
        <Stack direction="column" className={classes.card} spacing={1} justifyContent="stretch">
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-start" width="100%">
                <Email style={{ width: 32, height: 32, stroke: 'rgb(163, 167, 172)' }} />
                <Typography className={classes.title}>
                    {`Emails for  `}
                    <Typography component="span" className={classes.label}
                        onClick={() => redirectToDashboard(dashboard.id)}
                        sx={{ cursor: 'pointer' }}
                        display="inline">
                        {dashboard.title}
                    </Typography>
                </Typography>
            </Stack>
            <Divider sx={{ width: '100%' }} />
            <Box className={classes.body}>
                {emailActivities.map((emailBody, i, arr) => (
                    <Fragment key={'emails-content-card-192-' + emailBody?.id}>
                        <Stack direction="row" alignItems="flex-start" width="100%" >
                            {emailActivities.length > 1 && (
                                <Divider orientation="vertical" sx={{ height: 'calc(100% - 36px)', alignSelf: 'stretch', translate: '16px 40px' }} flexItem />
                            )}
                            <Box width="40px" mt="6px" pl="2px">
                                <EmailIcon className={classes.icon} sx={{ fill: '#7bd4d4 !important'}} />
                            </Box>
                            <Stack direction="column" spacing="4px" alignItems="flex-start" justifyContent="center" width="100%" py={1}>
                                <Stack direction="row" alignItems="center" justifyContent="space-between" pl={1} width="100%">
                                    <Typography className={classes.subject} width="100%">
                                        {emailBody?.subject?.replace('Fwd:', '')?.replace('Fw:', '') || '<Untitled>'}
                                    </Typography>
                                    <Typography className={classes.timestamp} width="30%">
                                        {`${moment(emailBody?.createdAt).fromNow()}`}
                                    </Typography>
                                </Stack>
                                <Stack direction="row" alignItems="center" justifyContent="space-between" pl={1} width="100%">
                                    <Stack direction="row" alignItems="center" justifyContent="flex-start">
                                        <Typography className={classes.ownership}>
                                            {'From: '}
                                            <Typography component="span" className={classes.owner} display="inline" pl="4px">
                                                {!!emailBody?.from ? emailBody?.from.replace(/<.*?>/g, '').trim() : 'N/A'}
                                            </Typography>
                                        </Typography>
                                    </Stack>
                                    <Stack direction="row" alignItems="center" justifyContent="flex-end">
                                        <IconButton sx={{ width: 24, height: 'auto', padding: 'unset' }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                e.preventDefault();
                                                setExpanded(prev => ({ ...prev, [i]: !prev[i] }));
                                            }} disableRipple>
                                            <ExpandMoreIcon style={{ transform: !isExpanded?.[i] ? 'none' : 'rotate(180deg)' }} />
                                        </IconButton>
                                    </Stack>
                                </Stack>
                                <Box width="100%" height="100%" pt={1}>
                                    <Collapse in={isExpanded?.[i] ?? false} collapsedSize={0}>
                                        <EmailContent content={emailBody!} />
                                    </Collapse>
                                </Box>
                            </Stack>
                        </Stack>
                        {(i < arr.length-1) && (<Divider sx={{ width: '100%' }} />)}
                    </Fragment>
                ))}
            </Box>
        </Stack>
    </>);
};

export default memo(EmailsContentCard);
